import React, {Suspense, useRef} from 'react'
import {BrowserRouter, useHistory} from 'react-router-dom'
import './App.scss'
import '../i18n'
import {Routes} from './routing/Routes'
import AuthInit from './modules/auth/redux/AuthInit'
import {LayoutProvider, LayoutSplashScreen} from '../_metronic/layout/core'
import SubscriptionsContext from '../context/SubscriptionsContext'
import {QueryParamProvider} from 'use-query-params'
import {ReactRouter5Adapter} from 'use-query-params/adapters/react-router-5'
import qs from 'query-string'
import {SSO_ENABLED} from '../setup/keycloak/authConfig'

type Props = {
  basename: string
}

// Under SSO there is no in-app `/auth/login` page (Keycloak hosts login). Dozens of legacy
// `auth:false` handlers still call `history.push('/auth/login')` when a not-yet-dual-mode backend
// rejects the KC token — which bounces to `/auth/login`, then `PrivateRoutes` redirects `/auth` back
// to `/dashboard`, which remounts and re-fires the failing calls: an infinite dashboard↔login toggle.
// We patch the shared BrowserRouter history instance ONCE so every navigation to `/auth/login`
// (push or replace, from any of the ~270 call sites) is a no-op under SSO. Uses BrowserRouter's own
// history object (via context), so there's no `history` package version coupling.
const isLoginPath = (to: unknown): boolean => {
  const path = typeof to === 'string' ? to : (to as {pathname?: string})?.pathname || ''
  return typeof path === 'string' && path.indexOf('/auth/login') !== -1
}

const HistoryGuard: React.FC = () => {
  const history = useHistory()
  const patched = useRef(false)
  if (SSO_ENABLED && !patched.current) {
    patched.current = true
    const push = history.push.bind(history)
    const replace = history.replace.bind(history)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    history.push = ((to: any, state?: any) => (isLoginPath(to) ? undefined : push(to, state))) as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    history.replace = ((to: any, state?: any) =>
      isLoginPath(to) ? undefined : replace(to, state)) as any
  }
  return null
}

const App: React.FC<Props> = ({basename}) => {
  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <BrowserRouter basename={basename}>
        <HistoryGuard />
        <QueryParamProvider
          adapter={ReactRouter5Adapter}
          options={{
            searchStringToObject: qs.parse,
            objectToSearchString: qs.stringify,
          }}
        >
          <LayoutProvider>
            <AuthInit>
              <SubscriptionsContext>
                <Routes />
              </SubscriptionsContext>
            </AuthInit>
          </LayoutProvider>
        </QueryParamProvider>
      </BrowserRouter>
    </Suspense>
  )
}

export {App}
